import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import classNames from "classnames";

import { getHourlyEmissions } from "utils/emissions/getHourlyEmissions";
import {
    calculateEmissions,
    getEnergyWebsite,
    getDailyDataByYear,
    getDailyDataByMonth,
    getDailyConsumptionByMonth,
    calcTOUCostEstimate,
    calcTieredCost,
    provinceDetails,
} from "utils/heatmap/heatmapFunctions";
import { getDetailedHourlyResults } from "utils/results/api";
import { getPackageRunId } from "utils/results/runIdentity";
import { useStateSelector } from "utils/field";

import Checkbox from "components/Input/Checkbox";
import Select from "components/Input/Select";
import Heatmap from "components/Heatmap/Heatmap";
import Button from "components/Button";
import EmissionsToggle from "components/EmissionsToggle";
import TabHeader from "components/TabHeader";
import HourlyDetails from "features/Model/Review/Details/HourlyDetails";
import DetailsDrawer from "components/DetailsDrawer";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import OpenIcon from "assets/images/icons/JSX/OpenDrawer";

const elecLoadHeader = "Hourly Electrical Load (kW)";
const emissionsHeader = "Hourly Emissions from Electricity Usage (gCO₂)";

// const getVehicleResults = (vehicles) => {
//     let totalKilometersDriven = 0;
//     let totalElectricityConsumption = 0;
//     let totalFuelConsumption = 0;
//     let totalEmissions = 0;
//     let totalFuelCost = 0;
//     let evProfile = null;

//     let numOfElectricVehicles = 0;

//     Object.keys(vehicles).forEach((key) => {
//         //for each vehicle...
//         const vehicle = vehicles[key]; //get individual vehicle
//         const { type: vehicleType } = vehicle; //get vehicle type

//         if (vehicleType === "ev") {
//             numOfElectricVehicles += 1; //count vehicle

//             //const { evProfile } = vehicle;

//             //get ev results
//             const {
//                 annualDistanceDriven: evDistanceDriven,
//                 annualEmissions: evEmissions,
//                 annualFuelConsumption: evElectricityConsumption,
//                 annualFuelCost: evFuelCost,
//                 evProfile: evProfileForSingleEV,
//             } = vehicle;

//             if (evElectricityConsumption != null) {
//                 //if ev has data (only check one because its triggered by having at least distance calculated)
//                 //add consumption to total value
//                 totalKilometersDriven += evDistanceDriven;
//                 totalElectricityConsumption += evElectricityConsumption;
//                 totalEmissions += evEmissions;
//                 totalFuelCost += evFuelCost;

//                 //initialize evProfile with the first EV's profile
//                 if (evProfile === null) {
//                     evProfile = evProfileForSingleEV.slice(); // Copy the array to avoid modifying the original
//                 } else {
//                     //ddd the hourly data from the current EV to the total profile
//                     evProfile = evProfile.map((value, index) => value + evProfileForSingleEV[index]);
//                 }
//             } //else warn user that data is invalid to add to overall consumption??

//             // } else if (vehicleType === 'hybrid') {

//             //     const { //get hybrid results
//             //         annualDistanceDriven: hybridDistanceDriven,
//             //         annualEmissions: hybridEmissions,
//             //         annualFuelConsumption: hybridFuelConsumption,
//             //         annualElectricityConsumption: hybridElectricityConsumption,
//             //         annualFuelCost: hybridFuelCost
//             //      } = vehicle;

//             //     if (hybridFuelConsumption != null) { //if hybrid has data
//             //         //add consumption to total value
//             //         totalKilometersDriven += hybridDistanceDriven;
//             //         totalElectricityConsumption += hybridElectricityConsumption;
//             //         totalFuelConsumption += hybridFuelConsumption;
//             //         totalEmissions += hybridEmissions;
//             //         totalFuelCost += hybridFuelCost;
//             //     }

//             // } else if (vehicleType === 'ice') {

//             //     const { //get ice results
//             //         annualDistanceDriven: iceDistanceDriven,
//             //         annualEmissions: iceEmissions,
//             //         annualFuelConsumption: iceFuelConsumption,
//             //         annualFuelCost: iceFuelCost
//             //     } = vehicle;
//             //     if (iceFuelConsumption != null) { //if ice has data
//             //         //add consumption to total value
//             //         totalKilometersDriven += iceDistanceDriven;
//             //         totalFuelConsumption += iceFuelConsumption;
//             //         totalEmissions += iceEmissions;
//             //         totalFuelCost += iceFuelCost;
//             //     }
//         }
//     });

//     return {
//         totalKilometersDriven,
//         totalElectricityConsumption,
//         totalFuelConsumption,
//         totalEmissions,
//         totalFuelCost,
//         numOfElectricVehicles,
//         evProfile,
//     };
// };

const HourlyAnalysis = ({
    electricalLoad,
    maxHourlyLoad, //Across all upgrade cases, so we can set a consistent scale
    upgradePackageId,
    //upgradePackages,
    resultsCase,
    transportationResults: { totalAnnualElectricityConsumption = 0, totalEvProfile = [] } = {},
}) => {
    const { selectedPackage = "base" } = useStateSelector("results");
    const {
        modelId,
        modelDetails: {
            lastCliRun: { cliId: cliIdData = {}, datetime: dateRun = "" } = {},
            simulationRunning = false,
        } = {},
    } = useStateSelector("model");

    const weatherRegion = useStateSelector("model.modelData.location.weather.region") || {};
    const weatherProvTerrId = weatherRegion.id ?? 0;

    const cliId = getPackageRunId(cliIdData, upgradePackageId);

    const vehicles = useStateSelector(`model.modelData.transportation.vehicles`) || {};

    const [open, toggleOpen] = useState(false);
    const [csvSubmitting, setCsvSubmitting] = useState(false);
    const [normalizeHourlyScale, setNormalizeHourlyScale] = useState(false);
    const [includeEV, setIncludeEV] = useState(false); //Include EV in Hourly Loads

    const hasEV = totalAnnualElectricityConsumption !== 0;
    const electricLoadWithEV = electricalLoad.map((value, index) => value + (totalEvProfile?.[index] || 0));

    const totalElectricalLoad = includeEV ? electricLoadWithEV : electricalLoad;

    const peakWinterLoad = Math.max(...totalElectricalLoad.slice(0, 2880), ...totalElectricalLoad.slice(7296, 8760));
    const peakSummerLoad = Math.max(...totalElectricalLoad.slice(2880, 7296));

    const peakOverallLoad = Math.max(...totalElectricalLoad);
    const averageLoad = totalElectricalLoad.reduce((sum, curr) => sum + curr, 0) / totalElectricalLoad.length;
    const loadFactor = (100 * averageLoad) / peakOverallLoad;

    const peakSolarExport = Math.min(...totalElectricalLoad);

    const [heatmapData, setHeatmapData] = useState(totalElectricalLoad);
    const [selectedDataset, setSelectedDataset] = useState("hourlyElectricalLoad");
    const [selectedEmissionFactor, setSelectedEmissionFactor] = useState("hefFf");
    const [selectedEmissionYear, setSelectedEmissionYear] = useState(2023);
    const [chartHeader, setChartHeader] = useState(elecLoadHeader);

    const [electricityRateSet, setElectrictyRateSet] = useState({
        onPeak: 0.17,
        midPeak: 0.113,
        offPeak: 0.082,
        tier1: 0.098,
        tier2: 0.115,
    });

    const dailyDataByYear = getDailyDataByYear(totalElectricalLoad);
    const dailyDataByMonth = getDailyDataByMonth(dailyDataByYear);
    const dailyConsumptionByMonth = getDailyConsumptionByMonth(dailyDataByMonth);

    let hourlyEmissionFactors = getHourlyEmissions({
        year: selectedEmissionYear,
        type: selectedEmissionFactor,
    }); //[gCO2/kWh]

    let emissions = calculateEmissions(totalElectricalLoad, hourlyEmissionFactors); //[gCO2]

    const TOUCostEstimate = calcTOUCostEstimate(weatherProvTerrId, electricityRateSet, dailyConsumptionByMonth);

    const TieredCostEstimate = calcTieredCost(dailyConsumptionByMonth, electricityRateSet, weatherProvTerrId);

    const numberFormatter = (value, decimal) => {
        if (value >= 100000) {
            return "+$99,999";
        } else if (value <= -9) {
            return "-$99,999";
        } else if (isNaN(value)) {
            return "...";
        }

        return "$" + parseFloat(parseFloat(value).toFixed(decimal)).toLocaleString("en-IN", { useGrouping: true });
    };

    const hasSolar = Math.min(...electricalLoad) < 0 ? true : false;

    const summedHourlyEmissions = emissions.slice(0, 8760).reduce((partialSum, a) => partialSum + a, 0) / 1000000; //[tCO2/y]

    const summedAnnualEmissions =
        ((hourlyEmissionFactors.reduce((sum, factor) => {
            return sum + factor;
        }, 0) /
            hourlyEmissionFactors.length) *
            totalElectricalLoad.reduce((sum, emission) => {
                return sum + emission;
            }, 0)) /
        1000000;

    const emissionFactorYears = [...Array(14).keys()].map((i) => ({ label: `${i + 2010}`, value: i + 2010 })).reverse();

    const handleChangeDataSet = (value) => {
        setSelectedDataset(value);
        if (value === "hourlyEmissions") {
            setHeatmapData(emissions);
            setChartHeader(emissionsHeader);
            if (includeEV === true) {
                setIncludeEV(false); //uncheck so the data won't be stuck with EV load
            }
        } else if (value === "hourlyElectricalLoad") {
            setHeatmapData(totalElectricalLoad);
            setChartHeader(elecLoadHeader);
        }
    };

    const handleEvCheckboxChange = (val) => {
        setIncludeEV(val);
        setHeatmapData(totalElectricalLoad);
        setSelectedDataset("hourlyElectricalLoad");
        setChartHeader(elecLoadHeader);
    };

    const handleChangeEmissionFactor = (value) => {
        setSelectedEmissionFactor(value);
        setHeatmapData(
            calculateEmissions(totalElectricalLoad, getHourlyEmissions({ year: selectedEmissionYear, type: value }))
        );
    };

    const handleChangeEmissionYear = (value) => {
        setSelectedEmissionYear(value);
        setHeatmapData(
            calculateEmissions(
                totalElectricalLoad,
                getHourlyEmissions({
                    year: value,
                    type: selectedEmissionFactor,
                })
            )
        );
    };

    useEffect(() => {
        setHeatmapData(includeEV ? electricLoadWithEV : electricalLoad);
        setSelectedDataset("hourlyElectricalLoad");
        setChartHeader(elecLoadHeader);
    }, [electricalLoad, includeEV]);

    const getCSV = async () => {
        setCsvSubmitting(true);

        const { data: { hourlyResults = {} } = {} } = await getDetailedHourlyResults({
            modelId,
            cliId,
            resultsCase,
            upgradePackageId,
        });

        if (selectedDataset !== "hourlyEmissions" && !isEmpty(hourlyResults)) {
            const {
                hourlyElectricalLoadkWh = [],
                hourlyBaseLoadskWh = [],
                hourlyVentkWh = [],
                hourlyDhwkWh = [],
                hourlyHeatingkWh = [],
                hourlyCoolingkWh = [],
                hourlySolarPVkWh = [],
                //hourlyEvLoadProfile = [],
                dbTemperature = [],
                totalHourlyHeatingLoad = [],
                // heatingEfficiencyOutput = [],
                // totalHourlyCoolingLoad = [],
                // coolingEfficiencyOutput = [],
                // hourlyUnscaledElecHeatingConsumption = [],
                // hourlyUnscaledCoolingConsumption = [],
            } = hourlyResults;

            const csvHeader = `hour,hourlyBaseLoadskWh,hourlyVentkWh,hourlyDhwkWh,hourlyHeatingkWh,hourlyCoolingkWh,hourlySolarPVkWh,hourlyElectricalLoadkWh,hourlyEvLoadkWh,totalHourlyHeatingLoadW,ambientTemperatureC`;
            // `dbTemperature,totalHourlyHeatingLoad,heatingEfficiencyOutput,totalHourlyCoolingLoad,coolingEfficiencyOutput,hourlyUnscaledElecHeatingConsumption,hourlyUnscaledCoolingConsumption,`

            const csv = hourlyElectricalLoadkWh.reduce((cache, curr, i) => {
                return (
                    `${cache}\r\n${i},${hourlyBaseLoadskWh[i]},${hourlyVentkWh[i]},${hourlyDhwkWh[i]},${hourlyHeatingkWh[i]},${hourlyCoolingkWh[i]},${hourlySolarPVkWh[i]},${curr},` +
                    `${totalEvProfile?.[i] || 0},${totalHourlyHeatingLoad[i]},${dbTemperature[i]},`
                );
            }, csvHeader);

            let cb = new Blob([csv], { type: "text/csv" });

            let url = window.URL.createObjectURL(cb);
            let anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `HourlyElectricalLoadEndUse_kWh.csv`;

            anchor.click();
            window.URL.revokeObjectURL(url);
            anchor.remove();
            setCsvSubmitting(false);
            return;
        }

        const csvHeader =
            selectedDataset === "hourlyEmissions" ? "HourlyElectricalEmissions_gCO2" : "HourlyElectricalLoad_kWh";
        let csv = `${csvHeader} \r\n`;
        for (let row of heatmapData) {
            csv += row + "";
            csv += "\r\n";
        }

        let cb = new Blob([csv], { type: "text/csv" });

        let url = window.URL.createObjectURL(cb);
        let anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${csvHeader}.csv`;

        anchor.click();
        window.URL.revokeObjectURL(url);
        anchor.remove();
        setCsvSubmitting(false);
        return;
    };

    const [check1, setCheck1] = useState(true);
    const [check2, setCheck2] = useState();

    const handleRadioClick = (event) => {
        if (check1 === true) {
            setCheck1(false);
            setCheck2(true);
        } else if (check2 === true) {
            setCheck2(false);
            setCheck1(true);
        }
    };

    const isValidPriceProvince = Object.keys(provinceDetails).includes(weatherProvTerrId.toString());

    return (
        <>
            <div className={classes.hourlyAnalysisHeader}>
                <TabHeader
                    className={classes.infoDrawer}
                    noBorder
                    title="Hourly Analysis"
                    detailsTrigger={() => toggleOpen(true)}
                    detailsText="About this calculation"
                />
                <DetailsDrawer open={open} close={() => toggleOpen(false)} title="About this calculation">
                    <HourlyDetails />
                    <div className={classes.buttons}>
                        <Button large type="hollow" onClick={() => toggleOpen(false)}>
                            Close
                        </Button>
                    </div>
                </DetailsDrawer>
            </div>
            <div className={classes.heatmap}>
                <div className={classes.heatmapRow}>
                    {electricalLoad.length > 0 && (
                        <div className={`${classes.selectorRow}`}>
                            <Select
                                className={`${classes.selection} ${classes.heatmapSelect} `}
                                name="hourlyData"
                                label="Data Set"
                                input={{
                                    value: selectedDataset,
                                    onChange: handleChangeDataSet,
                                }}
                                options={[
                                    {
                                        value: "hourlyElectricalLoad",
                                        label: "Electrical Load",
                                    },
                                    ...(weatherProvTerrId === 5
                                        ? [
                                              {
                                                  value: "hourlyEmissions",
                                                  label: "Emissions",
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                            {selectedDataset === "hourlyElectricalLoad" && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "0.1rem",
                                        width: "fit-content",
                                    }}
                                >
                                    <Checkbox
                                        className={classes.scaleToggle}
                                        name="normalizeHourlyScale"
                                        label="Normalize Scale"
                                        large
                                        input={{
                                            onChange: (event) => setNormalizeHourlyScale(!normalizeHourlyScale),
                                            value: normalizeHourlyScale,
                                        }}
                                    />
                                    {hasEV ? (
                                        <Checkbox
                                            className={classes.scaleToggle}
                                            name="includeEV"
                                            label="Include EV"
                                            large
                                            input={{
                                                onChange: (val) => handleEvCheckboxChange(val),
                                                value: includeEV,
                                            }}
                                        />
                                    ) : null}
                                </div>
                            )}
                            <Button className={classes.CSVbutton} onClick={getCSV} disabled={csvSubmitting}>
                                {csvSubmitting ? "Fetching Data..." : "Download CSV Data"}
                            </Button>
                        </div>
                    )}
                </div>
                <div className={classes.heatmapColumn}>
                    <div className={classes.secretDiv}>
                        <div className={classes.emissionsButtons}>
                            {selectedDataset === "hourlyEmissions" && (
                                <>
                                    <EmissionsToggle
                                        handleChangeEmissionFactor={handleChangeEmissionFactor}
                                        handleRadioClick={handleRadioClick}
                                        check1={check1}
                                        check2={check2}
                                    />
                                    <Select
                                        className={`${classes.selection} ${classes.heatmapSelect} `}
                                        style={{ innerHeight: "2.5rem" }}
                                        input={{
                                            value: selectedEmissionYear,
                                            onChange: handleChangeEmissionYear,
                                        }}
                                        options={emissionFactorYears}
                                        setValue={selectedEmissionYear}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    <Heatmap
                        hourlyData={heatmapData}
                        dataSet={chartHeader}
                        maxHourlyLoad={maxHourlyLoad}
                        normalizeHourlyScale={normalizeHourlyScale}
                    />
                    {electricalLoad.length > 0 && (
                        <div
                            className={`${classes.totals} ${classes.heatmapInfo}`}
                            //style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", //'1fr 1fr 1fr 1fr',
                                gridGap: "0.5rem",
                            }}
                        >
                            {selectedDataset === "hourlyElectricalLoad" ? (
                                <>
                                    <div>
                                        <p>Winter Peak Load</p>
                                        <div>
                                            <h1>{peakWinterLoad.toFixed(2)}</h1>
                                            <p>kW</p>
                                        </div>
                                    </div>
                                    <div>
                                        <p>Summer Peak Load</p>
                                        <div>
                                            <h1>{peakSummerLoad.toFixed(2)}</h1>
                                            <p>kW</p>
                                        </div>
                                    </div>
                                    {hasSolar && (
                                        <div>
                                            <p>Peak Solar Export</p>
                                            <div>
                                                <h1>{Math.abs(peakSolarExport).toFixed(2)}</h1>
                                                <p>kW</p>
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <p>Load Factor</p>
                                        <div>
                                            <h1>{loadFactor.toFixed(2)}</h1>
                                            <p>%</p>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <p>Total Hourly Emissions</p>
                                        <div>
                                            <h1>{summedHourlyEmissions.toFixed(2)}</h1>
                                            <p>tCO₂/y</p>
                                        </div>
                                    </div>
                                    <div>
                                        <p>Total Annual Emissions</p>
                                        <div>
                                            <h1>{summedAnnualEmissions.toFixed(2)}</h1>
                                            <p>tCO₂/y</p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {isValidPriceProvince && (
                    <>
                        <div className={classes.priceHeader}>
                            <h3>Electricity Prices for {provinceDetails[weatherProvTerrId].name}</h3>
                            <a target="_blank" className={classNames(classes.elecLink, sharedClasses.externalLink)} href={getEnergyWebsite(weatherProvTerrId)}>
                                <OpenIcon />
                            </a>
                        </div>
                        <div className={classes.heatmapRow}>
                            <div className={classes.heatmapRow}>
                                {provinceDetails[weatherProvTerrId].hasTOU && (
                                    <div className={classes.costPanel}>
                                        <div className={`${classes.heatmapRow} ${classes.costPanelInner}`}>
                                            <div className={classes.heatmapColumn}>
                                                <div className={`${classes.heatmapRow} ${classes.priceInput}`}>
                                                    <p>On-peak Price ($/kWh)</p>
                                                    <input
                                                        className={classes.electRateInput}
                                                        type="number"
                                                        onChange={(event) => {
                                                            setElectrictyRateSet({
                                                                ...electricityRateSet,
                                                                onPeak: parseFloat(event.target.value),
                                                            });
                                                        }}
                                                        defaultValue={electricityRateSet.onPeak}
                                                    />
                                                </div>
                                                <div className={`${classes.heatmapRow} ${classes.priceInput}`}>
                                                    <p>Mid-peak Price ($/kWh)</p>
                                                    <input
                                                        className={classes.electRateInput}
                                                        type="number"
                                                        onChange={(event) => {
                                                            setElectrictyRateSet({
                                                                ...electricityRateSet,
                                                                midPeak: parseFloat(event.target.value),
                                                            });
                                                        }}
                                                        defaultValue={electricityRateSet.midPeak}
                                                    />
                                                </div>
                                                <div className={`${classes.heatmapRow} ${classes.priceInput}`}>
                                                    <p>Off-peak Price ($/kWh)</p>
                                                    <input
                                                        style={{ marginTop: "0.5rem" }}
                                                        className={classes.electRateInput}
                                                        type="number"
                                                        onChange={(event) => {
                                                            setElectrictyRateSet({
                                                                ...electricityRateSet,
                                                                offPeak: parseFloat(event.target.value),
                                                            });
                                                        }}
                                                        defaultValue={electricityRateSet.offPeak}
                                                    />
                                                </div>
                                            </div>
                                            <div className={classes.costEstimate}>
                                                <p>Time-Of-Use Annual Cost</p>
                                                <h1>{numberFormatter(Math.max(0, TOUCostEstimate), 0)}</h1>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {provinceDetails[weatherProvTerrId].hasTiered && (
                                    <div className={classes.costPanel}>
                                        <div className={`${classes.heatmapRow} ${classes.costPanelInner}`}>
                                            <div className={classes.heatmapColumn}>
                                                <div
                                                    className={`${classes.heatmapRow} ${classes.priceInput}`}
                                                    style={{ marginTop: "0.5rem" }}
                                                >
                                                    <p>Tier 1 Price ($/kWh)</p>
                                                    <input
                                                        className={classes.electRateInput}
                                                        type="number"
                                                        onChange={(event) => {
                                                            setElectrictyRateSet({
                                                                ...electricityRateSet,
                                                                tier1: parseFloat(event.target.value),
                                                            });
                                                        }}
                                                        defaultValue={electricityRateSet.tier1}
                                                    />
                                                </div>
                                                <div className={`${classes.heatmapRow} ${classes.priceInput}`}>
                                                    <p>Tier 2 Price ($/kWh)</p>
                                                    <input
                                                        className={classes.electRateInput}
                                                        type="number"
                                                        onChange={(event) => {
                                                            setElectrictyRateSet({
                                                                ...electricityRateSet,
                                                                tier2: parseFloat(event.target.value),
                                                            });
                                                        }}
                                                        defaultValue={electricityRateSet.tier2}
                                                    />
                                                </div>
                                            </div>
                                            <div className={classes.costEstimate}>
                                                <p>Tiered Annual Cost</p>
                                                <h1>{numberFormatter(Math.max(0, TieredCostEstimate), 0)}</h1>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default HourlyAnalysis;
